import { Link } from "react-router-dom";
import type { ReactNode } from "react";

import { firstNameFromDisplayName, isPersonOwnedWipDocument } from "@integrator-src/api/document/document-owner-label";
import {
  skuMegaventoryHoverTitle,
  type ControlledDocument,
} from "@/lib/documents";
import { buildMrpSkuDetailPath } from "@/lib/mrp";
import { HubUserAvatar } from "@/components/HubUserAvatar";
import { HoverHintContent } from "@/components/ui/title-help";

const DOCUMENT_TARGET_PILL_CLASS =
  "inline-flex rounded-full bg-brand-50 px-2 py-1 text-xs font-medium text-brand-800";

/** First SKU target on the document, else legacy `document.sku`. */
export function primarySkuFromControlledDocument(document: ControlledDocument): string | null {
  const skuTarget = document.targets?.find((target) => target.target_type === "sku" && target.sku?.trim());
  if (skuTarget?.sku) {
    return skuTarget.sku.trim();
  }
  const legacySku = document.sku?.trim();
  return legacySku && legacySku.length > 0 ? legacySku : null;
}

export function controlledDocumentTargetsAsPills(
  document: ControlledDocument,
  options?: { linkCollections?: boolean; linkSkus?: boolean; stopPropagation?: boolean },
): ReactNode {
  const linkCollections = options?.linkCollections ?? false;
  const linkSkus = options?.linkSkus ?? false;
  const stopPropagation = options?.stopPropagation ?? false;
  const targets = document.targets ?? [];
  const pills: ReactNode[] = [];
  for (const target of targets) {
    if (target.target_type === "collection" && target.product_collection_id) {
      const slug = target.product_collection_code?.trim() || target.product_collection_id.slice(0, 8);
      const collectionHoverHint = target.product_collection_name ?? slug;
      if (linkCollections) {
        pills.push(
          <HoverHintContent key={`collection-${target.product_collection_id}`} hoverHint={collectionHoverHint}>
            <Link
              to={`/documents/collections/${target.product_collection_id}`}
              className={`${DOCUMENT_TARGET_PILL_CLASS} hover:bg-brand-100`}
              onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
            >
              {slug}
            </Link>
          </HoverHintContent>,
        );
      } else {
        pills.push(
          <HoverHintContent key={`collection-${target.product_collection_id}`} hoverHint={collectionHoverHint}>
            <span className={DOCUMENT_TARGET_PILL_CLASS}>{slug}</span>
          </HoverHintContent>,
        );
      }
    } else if (target.target_type === "sku" && target.sku) {
      const slug = target.sku;
      const skuTitle = skuMegaventoryHoverTitle(target.mv_description, target.mv_variant) ?? slug;
      if (linkSkus) {
        pills.push(
          <HoverHintContent key={`sku-${target.sku}`} hoverHint={skuTitle}>
            <Link
              to={buildMrpSkuDetailPath(slug)}
              className={`${DOCUMENT_TARGET_PILL_CLASS} hover:bg-brand-100`}
              onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
            >
              {slug}
            </Link>
          </HoverHintContent>,
        );
      } else {
        pills.push(
          <HoverHintContent key={`sku-${target.sku}`} hoverHint={skuTitle}>
            <span className={DOCUMENT_TARGET_PILL_CLASS}>{slug}</span>
          </HoverHintContent>,
        );
      }
    }
  }
  if (pills.length === 0) {
    if (document.sku) {
      const slug = document.sku;
      const skuTitle = skuMegaventoryHoverTitle(document.sku_mv_description, document.sku_mv_variant) ?? slug;
      if (linkSkus) {
        return (
          <HoverHintContent hoverHint={skuTitle}>
            <Link to={buildMrpSkuDetailPath(slug)} className={`${DOCUMENT_TARGET_PILL_CLASS} hover:bg-brand-100`}>
              {slug}
            </Link>
          </HoverHintContent>
        );
      }
      return (
        <HoverHintContent hoverHint={skuTitle}>
          <span className={DOCUMENT_TARGET_PILL_CLASS}>{slug}</span>
        </HoverHintContent>
      );
    }
    if (document.product_family_id && document.product_family_code) {
      const slug = document.product_family_code;
      const productFamilyHoverHint = document.product_family_name ?? slug;
      return (
        <HoverHintContent hoverHint={productFamilyHoverHint}>
          <span className={DOCUMENT_TARGET_PILL_CLASS}>{slug}</span>
        </HoverHintContent>
      );
    }
    if (isPersonOwnedWipDocument(document)) {
      const firstName = firstNameFromDisplayName(document.owner_display_name, document.owner_email);
      const hoverHint = document.owner_display_name?.trim() || document.owner_email || firstName;
      return (
        <HoverHintContent hoverHint={hoverHint}>
          <span className={`${DOCUMENT_TARGET_PILL_CLASS} items-center gap-1`}>
            <HubUserAvatar
              avatarUrl={document.owner_avatar_url}
              label={hoverHint}
              size="sm"
              className="h-4 w-4 text-[9px]"
            />
            {firstName}
          </span>
        </HoverHintContent>
      );
    }
    return <span className="text-sm text-slate-500">Unassigned</span>;
  }
  return pills;
}
